import "./index.scss";
import "../../locales/i18n";

import { useMutation } from "@apollo/client";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import Close from "@/components/components/Close";
import { SwitchValue } from "@/components/components/Switch/switch.type";
import { ChatActive, ChatInActive } from "@/components/icons";
import AvailabilityToggle from "@/messenger/components/AvailabilityToggle";
import { representativeSettingsSelectors, updateRepresentativeSettingsAC } from "@/messenger/ducks/entities/representativeSettings";
import { saveRepresentativeIncomingAvailable } from "@/messenger/graphql/mutations";
import { TRepresentative } from "@/messenger/types/entities/representative";
import { getDomainHost } from "@/messenger/utils/helpers";
import { userSelector } from "@/messenger/ducks/selectors/general";

const AvailabilityButton = (): React.ReactElement => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const representativeSettings: TRepresentative[] = useSelector(representativeSettingsSelectors.selectAll);
    const inboundAvailability = representativeSettings[0]?.incomingEnabled;
    const isAutoResponderOn = false;
    
    const [isToggleOpen, setIsToggleOpen] = useState(false);
    const [showAvailability, setShowAvailability] = useState(true);
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef(null);
    const firstRender = useRef(true);

    const user = useSelector(userSelector);

    useEffect(() => {
        const host = getDomainHost();
        if (host.includes("freedomusa")) {
            setShowAvailability(false);
        }
    }, []);

    useEffect(() => {
        if (tooltipRef?.current && !inboundAvailability && firstRender.current) {
            ReactTooltip.show(tooltipRef.current);
            setTimeout(() => {
                ReactTooltip.hide(tooltipRef.current);
            }, 2000);
        }
    }, [firstRender.current, inboundAvailability, tooltipRef]);
    
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
        }
    }, [isToggleOpen]);

    const [updateInboundAvailability] = useMutation<{
        saveRepresentativeIncomingAvailable: boolean;
    }>(saveRepresentativeIncomingAvailable);

    const onOpen = () => {
        setIsToggleOpen(true);
    };
    const onHover = () => {
        setShowTooltip(true);
    };
    const onHoverHide = () => {
        setShowTooltip(false);
    };
    const onClose = () => {
        setIsToggleOpen(false);
    };
    
    const onInboundAvailabilityToggle = (value: SwitchValue) => {
        updateInboundAvailability({
            variables: {
                customerId: user.customerId,
                repId: user.identity,
                availability: value.on,
            },
        });
        dispatch(updateRepresentativeSettingsAC({ incomingEnabled: value.on }))
    };
    const onAwayToggle = (value: SwitchValue) => {
        console.log(`Away message: ${value.on}`);
    };

    return (showAvailability && <>
        <button 
            id={"AVAILABILITY_BUTTON"}
            aria-label={t("inbound availability button")}
            className={"c-availability-button__icon"} 
            onClick={onOpen}
            onMouseEnter={onHover}
            onMouseLeave={onHoverHide}
            data-testid="availability-button"
            ref={tooltipRef}
            data-tip
            data-for={"availability-reminder"}
        >
            {inboundAvailability ? <ChatActive /> : <ChatInActive />}
        </button>
        {!inboundAvailability && showTooltip && !isToggleOpen && 
        <ReactTooltip id={"availability-reminder"} place="top" effect="solid" className="c-availability-button__tooltip hover_tooltip">
            {t("Set if you are availabile to receive incoming messages")}
        </ReactTooltip>}
        {isToggleOpen && <Close isOpen={isToggleOpen} closeChange={onClose}>
            <AvailabilityToggle 
                onClose={onClose} 
                onToggleInbound={onInboundAvailabilityToggle} 
                onToggleAway={onAwayToggle} 
                isInboundOn={inboundAvailability}
                isAutoResponderOn={isAutoResponderOn} 
            />
        </Close>}
    </>);
};

export default AvailabilityButton;